import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, categoryNames } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'gaoxiao';
  const categoryVideos = useMemo(
    () => videos.filter((v) => v.category === currentSlug),
    [currentSlug]
  );

  const currentCategory = categories.find((c) => c.slug === currentSlug);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - 快猫短视频`,
    description: currentCategory?.description || '快猫短视频分类内容',
    url: `https://www.kuaimao.com/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '快猫短视频',
      url: 'https://www.kuaimao.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb />

        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="py-8 md:py-12">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                {currentCategory?.name}视频
              </h1>
              <p className="text-sm md:text-base text-foreground-500">
                {currentCategory?.description} · 共 {categoryVideos.length} 个视频
              </p>
            </div>

            <div className="pb-6 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      currentSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
                {categoryVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 快猫短视频`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <span className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded-md">
                        {video.duration}
                      </span>
                      {video.featured && (
                        <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs px-2 py-0.5 rounded-md">
                          精选
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <h2 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 leading-relaxed group-hover:text-primary-500 transition-colors">
                        {video.title}
                      </h2>
                      <p className="text-xs text-foreground-500 mb-3 line-clamp-2">{video.description}</p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i> {video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万` : video.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-heart-line"></i> {video.likes >= 10000 ? `${(video.likes / 10000).toFixed(0)}万` : video.likes}
                          </span>
                        </div>
                        <span className="text-foreground-400">{video.createdAt}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-vidicon-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}